// componentMap.ts
import EngagementLetterTemplateSelector from "./components/EngagementLetterTemplateSelector/EngagementLetterTemplateSelector";
import ClientSelector from "./components/ClientSelector/ClientSelector";
import ContractInformationEditor from "./components/ContractInformationEditor/ContractInformationEditor";
import AddendumEditor from "./components/AddendumEditor/AddendumEditor";
import {
  WizardComponentMap,
  WizardSharedData,
} from "../../reusable_components/WizardPageController/WizardPageController.utils";
import { TemplateOption } from "./components/EngagementLetterTemplateSelector/EngagementLetterTemplateSelector.utils";
import { FieldProps } from "../../../Reusables/components/InputFieldsController/InputFieldsController.utils";
import { format, parseISO } from "date-fns";

export interface EngagementLetterWizardSharedData extends WizardSharedData {
  templateId: number | undefined; // Template selector page validator
  templatesFetched: boolean; // Template selector cache flag
  templateOptions: TemplateOption[]; // Template selector cache data
  templateAllFields: any;
  templateFieldsMapping: any;
  selectedClientTypeCode: string;
  selectedClientPorterId: number;
  clientMasterIds: number[];
  selectedClientMasterId: number;
  clientsAllData: any;
  clientGroupData?: any;
  contractInfoEditorMessage: string;
  contractInfoEditorData: {
    [clientMasterId: number]: FieldProps[];
  };
  saveApiProgress?: boolean;
  draftApiProgress?: boolean;
  validations: any;
  isEditorLoading: boolean;
  sync: boolean;
  currentPage: string;
  disableSubmit?: boolean;
}

export const createNewEngagementLetterComponentMap: WizardComponentMap<EngagementLetterWizardSharedData> =
  {
    selectTemplate: EngagementLetterTemplateSelector,
    selectClient: ClientSelector,
    fillInfo: ContractInformationEditor,
    addendums: AddendumEditor,
  };

export interface CreateEngagementLetterWizardProps {
  show: boolean;
  onHide: () => void;
  setapiflag: (flag: boolean) => void;
  templateData?: any[];
  clientMasterId?: number | undefined;
  templateOptions?: TemplateOption[];
  templatesFetched?: boolean;
  engagementlettertemplates: (data: any[]) => void;
  isDraftEdit?: boolean;
  draftProcessedTemplateId?: number;
  draftTemplateId?: number;
}

export interface TemplateRuleDetailProps {
  TemplateRuleId: number;
  TemplateRuleDetailId: number;
  TemplateId: number;
  FirmId: number;
  Active: string;
  ConditionValue: string;
  DefaultValue: string;
  Operator: string;
  RuleName: string;
  SelectedFieldLabel: string;
  SelectedFieldValue: string;
  SubstitutionValue: string;
}

/**
 * Updates the current template fields mapping based on the values from provided fields.
 *
 * @param currentMapping - The existing template fields mapping object.
 * @param fields - List of form fields containing `allfields_key` and `value`.
 * @returns A new object with updated template field mappings.
 */
export const getUpdatedTemplateFieldsMapping = (
  currentMapping: Record<string, any>,
  fields: FieldProps[]
): Record<string, any> => {
  const updatedMapping = JSON.parse(JSON.stringify(currentMapping)); // Deep clone

  if (!Array.isArray(updatedMapping.SelectedFields)) {
    updatedMapping.SelectedFields = [];
  }
  
  fields.forEach(({ allfields_key, value }) => {
    Object.entries(updatedMapping).forEach(([section, items]) => {
      if (Array.isArray(items)) {
        updatedMapping[section] = items.map((item: any) => {
          // For rule-based fields
          if (
            section === "Rules" &&
            item.data?.SelectedFieldValue === allfields_key
          ) {
            return item.value !== value ? { ...item, value } : item;
          }

          // For normal fields
          if (
            (item.label === allfields_key || item.name === allfields_key) &&
            item.value !== value
          ) {
            return { ...item, value };
          }

          return item;
        });
      }
    });
  });

  fields.forEach((field) => {
    const fieldCode = field.allfields_code;

    if (!fieldCode) return;

    const exists = updatedMapping.SelectedFields.some(
      (item: any) =>
        item.code?.toLowerCase() === fieldCode.toLowerCase() ||
        item.name?.toLowerCase() === fieldCode.toLowerCase()
    );
    if (!exists) {
      updatedMapping.SelectedFields.push({
        id: field.allfields_id ?? field.id ?? fieldCode,
        code: fieldCode,
        name: field.allfields_key ?? fieldCode,
        label: field.label ?? field.allfields_key ?? fieldCode,
        type: field.type,
        value: field.value,
      });
    }
  });
  return updatedMapping;
};

export const isClientFormPopulated = (fields: FieldProps[] | undefined) =>
  Array.isArray(fields) &&
  fields.some((f) => f.value !== undefined && f.value !== "");

/**
 * Injects ELForm InputStructure.fields metadata into ONLY
 * templateFieldsMapping.SelectedFields[*].data.
 *
 * - Matches using allfields_code → primary key
 * - Falls back to label or name when needed
 */
export function mergeElFormIntoSelectedFields(
  templateMapping: Record<string, any>,
  elFormFields: any[]
) {
  if (!templateMapping?.SelectedFields || !Array.isArray(elFormFields)) {
    return templateMapping;
  }

  // Build lookup table
  const lookup: Record<string, any> = {};
  elFormFields.forEach((field) => {
    if (field.allfields_code)
      lookup[field.allfields_code.toLowerCase()] = field;
  });

  // Process ONLY SelectedFields
  const updatedSelectedFields = templateMapping.SelectedFields.map(
    (item: { code: string; label: string; name: string; data: any }) => {
      if (!item) return item;

      const match = lookup[item.code?.toLowerCase()];

      if (!match) return item;

      return {
        ...item,
        data: {
          ...(item.data || {}),
          ...match,
        },
      };
    }
  );

  return {
    ...templateMapping,
    SelectedFields: updatedSelectedFields,
  };
}

export function prepareFieldsForMailMerge(
  selectedFields: any[],
  elForms: any[]
): any[] {
  if (!Array.isArray(selectedFields)) return selectedFields;

  const fieldDefinitionMap: Record<string, any> = {};
  const runtimeValueMap: Record<string, any> = {};

  selectedFields.forEach((f) => {
    runtimeValueMap[f.code] = f.value;
  });

  // Build definition map (RULES only)
  if (elForms && elForms.length > 0) {
    const inputFields = elForms[0]?.InputStructure?.fields ?? [];
    inputFields.forEach((f: any) => {
      if (f.allfields_code) {
        fieldDefinitionMap[f.allfields_code] = f;
      }
    });
  }

  return selectedFields.reduce((acc: any[], sf) => {
    const fieldDef = fieldDefinitionMap[sf.code];

    if (!fieldDef) return acc;
    if (fieldDef.show === false) return acc;

    if (fieldDef.dependsOn) {
      const allowedValues =
        fieldDef.dependsOnVisibilityValues ??
        (fieldDef.dependsOnVisibilityValue
          ? [fieldDef.dependsOnVisibilityValue]
          : []);

      const parentValue = runtimeValueMap[fieldDef.dependsOn];

      if (
        allowedValues.length > 0 &&
        !allowedValues.includes(parentValue)
      ) {
        return acc;
      }
    }

    if (fieldDef.type !== "date") {
      acc.push(sf);
      return acc;
    }

    const cfg = fieldDef.dateConfig;
    if (!cfg || !sf.value) {
      acc.push(sf);
      return acc;
    }

    try {
      const parsed = parseISO(String(sf.value));
      const displayFormat = fieldDef.showTimeSelect
        ? cfg.displayFormatDateTime
        : cfg.displayFormatDate;

      acc.push({
        ...sf,
        value: format(parsed, displayFormat),
      });
    } catch {
      acc.push(sf);
    }

    return acc;
  }, []);
}